const normalizeToken = (value) => String(value || '').trim();
const normalizeUpper = (value) => normalizeToken(value).toUpperCase();
const normalizeLower = (value) => normalizeToken(value).toLowerCase();

const toNullableNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const parseDateOrNull = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getSportKey = (match) => {
  const sport = match?.sport;
  const sportValue = typeof sport === 'string'
    ? sport
    : (
      sport?.slug
      || sport?.name
      || sport?.name_ko
      || sport?.key
      || ''
    );
  return normalizeLower(
    sportValue
    || match?.sportSlug
    || match?.sport_slug
    || match?.sportKey
    || match?.sport_key
    || match?.sportName
    || match?.sport_name
    || match?.leagueSport
    || ''
  ).replace(/[_\s]+/g, '-');
};

const isSoccerLike = (sportKey) => /soccer|football|축구/.test(sportKey);
const isBasketballLike = (sportKey) => /basket|농구/.test(sportKey);
const isBaseballLike = (sportKey) => /baseball|야구/.test(sportKey);
const isHockeyLike = (sportKey) => /hockey|하키/.test(sportKey);
const isTennisLike = (sportKey) => /tennis|테니스/.test(sportKey);
const isVolleyballLike = (sportKey) => /volley|배구/.test(sportKey);
const isEsportsLike = (sportKey) => /esport|e-sport|e스포츠|이스포츠|valorant|cs2|cs:go|dota|league-of-legends|lol/.test(sportKey);

const SOCCER_PHASE_MAP = {
  H1: '전반',
  '1H': '전반',
  '1': '전반',
  HALF: '전반',
  FH: '전반',
  FIRST_HALF: '전반',
  '1ST_HALF': '전반',
  H2: '후반',
  '2H': '후반',
  '2': '후반',
  SH: '후반',
  SECOND_HALF: '후반',
  '2ND_HALF': '후반',
  HT: '하프타임',
  ET1: '연장 전반',
  ET2: '연장 후반',
  AET: '연장 종료',
  PEN: '승부차기',
};

const HOCKEY_PHASE_MAP = {
  P1: '1피리어드',
  P2: '2피리어드',
  P3: '3피리어드',
  '1': '1피리어드',
  '2': '2피리어드',
  '3': '3피리어드',
  OT: '연장',
};

export const getSportsnewSportLabel = (matchOrSport) => {
  const sportKey = typeof matchOrSport === 'string'
    ? normalizeLower(matchOrSport).replace(/[_\s]+/g, '-')
    : getSportKey(matchOrSport);
  if (isSoccerLike(sportKey)) return '축구';
  if (isBasketballLike(sportKey)) return '농구';
  if (isBaseballLike(sportKey)) return '야구';
  if (isHockeyLike(sportKey)) return '아이스하키';
  if (isVolleyballLike(sportKey)) return '배구';
  if (isTennisLike(sportKey)) return '테니스';
  if (isEsportsLike(sportKey)) return 'e스포츠';
  return typeof matchOrSport === 'string'
    ? normalizeToken(matchOrSport)
    : normalizeToken(
      matchOrSport?.sport?.name_ko
      || matchOrSport?.sport?.name
      || matchOrSport?.sportName
      || matchOrSport?.sport_name
      || matchOrSport?.leagueSport
    );
};

const getPeriodToken = (match) => normalizeUpper(
  match?.live_period
  || match?.period
  || match?.period_label
  || match?.periodLabel
  || match?.periodNumber
  || ''
).replace(/[\s-]+/g, '_');

const getBaseballSideLabel = (value) => {
  const token = normalizeUpper(value).replace(/[\s_-]+/g, '');
  if (!token) return '';
  if (/^(T|TOP|초)$/.test(token)) return '초';
  if (/^(B|BOT|BOTTOM|말)$/.test(token)) return '말';
  return '';
};

const getBaseballSideFromMatch = (match) => (
  getBaseballSideLabel(match?.inningHalf)
  || getBaseballSideLabel(match?.inning_half)
  || getBaseballSideLabel(match?.topBottom)
  || getBaseballSideLabel(match?.top_bottom)
  || getBaseballSideLabel(match?.half)
  || getBaseballSideLabel(match?.side)
  || getBaseballSideLabel(match?.live_clock)
  || getBaseballSideLabel(match?.clock)
);

const parseBaseballPeriod = (periodToken, match = {}) => {
  const raw = normalizeUpper(periodToken).replace(/[_-]+/g, ' ');
  const compact = raw.replace(/\s+/g, '');
  const patterns = [
    /^([TB])(\d{1,2})$/,
    /^(\d{1,2})([TB])$/,
    /^(TOP|BOT|BOTTOM)(\d{1,2})$/,
    /^(\d{1,2})(TOP|BOT|BOTTOM)$/,
  ];
  for (const pattern of patterns) {
    const match = compact.match(pattern);
    if (!match) continue;
    const inning = /^\d+$/.test(match[1]) ? match[1] : match[2];
    const side = /^\d+$/.test(match[1]) ? match[2] : match[1];
    return `${inning}회${side.startsWith('T') ? '초' : '말'}`;
  }
  const spaced = raw.match(/(\d{1,2})\s*(TOP|BOT|BOTTOM|초|말)|(?:TOP|BOT|BOTTOM|초|말)\s*(\d{1,2})/);
  if (spaced) {
    const inning = spaced[1] || spaced[3];
    const side = spaced[2] || spaced[0];
    return `${inning}회${/TOP|초/.test(side) ? '초' : '말'}`;
  }
  const inningOnly = compact.match(/^(\d{1,2})(?:ST|ND|RD|TH)?$/)?.[1]
    || toNullableNumber(match?.periodNumber)
    || toNullableNumber(match?.inning);
  if (!inningOnly) return '';
  const sideLabel = getBaseballSideFromMatch(match);
  return `${inningOnly}회${sideLabel}`;
};

const parseOrdinalNumber = (periodToken) => {
  const token = normalizeUpper(periodToken);
  return token.match(/(\d{1,2})/)?.[1] || '';
};

const getPeriodLabel = (match) => {
  const sportKey = getSportKey(match);
  const periodToken = getPeriodToken(match);
  if (!periodToken) return '';

  if (isSoccerLike(sportKey)) {
    return SOCCER_PHASE_MAP[periodToken] || periodToken;
  }

  if (isBasketballLike(sportKey)) {
    const q = periodToken.match(/Q(\d+)/)?.[1] || parseOrdinalNumber(periodToken);
    if (q) return `${q}쿼터`;
    if (periodToken.startsWith('OT')) return '연장';
    return periodToken;
  }

  if (isBaseballLike(sportKey)) {
    return parseBaseballPeriod(periodToken, match) || periodToken;
  }

  if (isHockeyLike(sportKey)) {
    return HOCKEY_PHASE_MAP[periodToken] || (parseOrdinalNumber(periodToken) ? `${parseOrdinalNumber(periodToken)}피리어드` : periodToken);
  }

  if (isTennisLike(sportKey) || isVolleyballLike(sportKey)) {
    const setNo = periodToken.match(/S(\d+)/)?.[1] || parseOrdinalNumber(periodToken);
    if (setNo) return `${setNo}세트`;
    return periodToken;
  }

  if (isEsportsLike(sportKey)) {
    const mapNo = periodToken.match(/M(?:AP)?_?(\d+)/)?.[1] || parseOrdinalNumber(periodToken);
    if (mapNo && /SET|세트/.test(periodToken)) return `${mapNo}세트`;
    if (mapNo) return `${mapNo}맵`;
    return periodToken;
  }

  return periodToken;
};

export const getLivePhaseLabel = (match) => {
  const sportKey = getSportKey(match);
  const status = normalizeUpper(match?.fixture_status || match?.status);
  const statusLabel = normalizeLower(match?.fixture_status_label);
  const periodLabel = getPeriodLabel(match);

  if (status === 'CANCELLED' || statusLabel.includes('cancel')) return '경기 취소';
  if (['CLOSED', 'ENDED', 'FINISHED', 'FT'].includes(status) || statusLabel.includes('ended') || statusLabel.includes('finished')) {
    return '경기 종료';
  }

  if (periodLabel === '하프타임' || statusLabel === 'ht' || statusLabel.includes('half_time') || statusLabel.includes('halftime')) {
    return '하프타임';
  }

  if (statusLabel.includes('quarter_break') || statusLabel.includes('period_break') || statusLabel.includes('intermission')) {
    return periodLabel ? `${periodLabel} 종료` : '인터벌';
  }

  if (periodLabel) return periodLabel;

  if (['SCHEDULED', 'OPEN', 'NOT_STARTED', 'PRE_MATCH'].includes(status) || statusLabel.includes('not_started') || statusLabel.includes('scheduled')) {
    return '경기 시작 전';
  }

  if (statusLabel.includes('live') || ['LIVE', 'IN_PLAY', 'INPLAY', 'PLAYING'].includes(status)) {
    return '경기중';
  }

  return '';
};

export const getLiveScore = (match) => {
  const home = toNullableNumber(match?.live_score_home);
  const away = toNullableNumber(match?.live_score_away);
  const hasScore = home !== null || away !== null;

  if (!hasScore) {
    return {
      hasScore: false,
      home: null,
      away: null,
      text: '- : -',
    };
  }

  return {
    hasScore: true,
    home: home ?? 0,
    away: away ?? 0,
    text: `${home ?? 0} : ${away ?? 0}`,
  };
};

export const getLiveClockText = (match, now = Date.now()) => {
  const sportKey = getSportKey(match);
  const rawClock = normalizeToken(match?.live_clock || match?.clock || match?.gameClock || match?.game_clock || '');
  const clockParts = rawClock.match(/^(\d{1,2}):(\d{2})$/);
  const numericClock = /^\d+(?:\.\d+)?$/.test(rawClock) ? toNullableNumber(rawClock) : null;
  const minute = toNullableNumber(match?.live_minute ?? match?.minute)
    ?? (clockParts ? Number(clockParts[1]) : null)
    ?? (isSoccerLike(sportKey) ? numericClock : null);
  const seconds = toNullableNumber(match?.live_seconds ?? match?.seconds) ?? (clockParts ? Number(clockParts[2]) : null);
  const phaseLabel = getLivePhaseLabel(match);

  if (minute === null && seconds === null) return '';
  if (phaseLabel === '하프타임' || phaseLabel === '경기 종료' || phaseLabel === '경기 취소') return '';
  if (isBaseballLike(sportKey) || isVolleyballLike(sportKey) || isTennisLike(sportKey) || isEsportsLike(sportKey)) return '';

  const baseSeconds = Math.max(0, Math.floor(minute ?? 0)) * 60 + Math.max(0, Math.floor(seconds ?? 0));
  if (!isSoccerLike(sportKey)) {
    if (minute === null) return '';
    if (seconds !== null || clockParts) {
      const mm = Math.max(0, Math.floor(minute));
      const ss = Math.max(0, Math.floor(seconds ?? 0));
      return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
    }
    return `${Math.max(0, Math.floor(minute))}분`;
  }

  const updatedAt = parseDateOrNull(match?.live_clock_updated_at);
  const canTick = updatedAt && minute !== null;
  const elapsedSeconds = canTick ? Math.max(0, Math.floor((now - updatedAt.getTime()) / 1000)) : 0;
  const totalSeconds = baseSeconds + elapsedSeconds;
  const mm = Math.floor(totalSeconds / 60);
  return `${mm}분`;
};

export const getLiveHeaderText = (match, now = Date.now()) => {
  const phaseLabel = getLivePhaseLabel(match);
  const clockText = getLiveClockText(match, now);
  if (phaseLabel && clockText) return `${phaseLabel} ${clockText}`;
  return phaseLabel || clockText || '';
};
